import { create } from "zustand";

import type { Announcement, AnnouncementLevel, AnnouncementReply } from "../api/types";
import { lang, locale, t } from "../i18n/i18n";
import { plural } from "../plural";

/** Annonce d'une maintenance de la production (registre F40, docs/11 §11.10) : logique pure du bandeau
 *  (AnnouncementBanner.tsx) et de la fenêtre de la préproduction (MaintenanceDialog.tsx).
 *
 *  L'annonce est publiée depuis la préproduction (mot de passe de promotion, service root) et lue par chaque onglet
 *  sur `GET /api/announcement` (publique : écran d'activation et lecture seule compris), toutes les 5 minutes et au
 *  retour sur la page. Rien n'est bloquant : serveur muet ou réponse illisible, la dernière annonce connue reste
 *  (mémoire de l'onglet, puis copie locale du poste : une page rechargée pendant la maintenance, serveur arrêté,
 *  l'annonce encore) ; aucune annonce, aucun bandeau. Le bandeau se ferme pour la session et revient à l'approche
 *  (15 minutes avant le début), puis au début ; il disparaît de lui-même à la fin. Heures montrées à l'heure de
 *  Paris (le serveur compte en UTC) ; compte à rebours d'après l'heure du serveur, corrigée de l'horloge du poste. */

export const ANNOUNCEMENT_EVERY_MS = 5 * 60_000;
export const ANNOUNCEMENT_MIN_GAP_MS = 30_000;
/** Le bandeau fermé revient à l'approche de la maintenance. */
export const IMMINENT_MS = 15 * 60_000;
export const LEVELS: readonly AnnouncementLevel[] = ["info", "outage"];
const INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:00Z$/;
const PARIS = "Europe/Paris";
/** Écart admis entre l'horloge du poste et celle du serveur (au-delà, réponse ignorée pour l'horloge). */
const MAX_OFFSET_MS = 7 * 86_400_000;

/** Phase de l'annonce : à venir, imminente (moins de 15 min), en cours ; null une fois finie. */
export type Phase = "upcoming" | "imminent" | "running";
const RANK: Record<Phase, number> = { upcoming: 0, imminent: 1, running: 2 };

// --- Lecture --------------------------------------------------------------------------------------------------------

/** Annonce reçue, contrôlée champ par champ ; null si elle est absente ou illisible. */
export function parseAnnouncement(value: unknown): Announcement | null {
  if (!value || typeof value !== "object") return null;
  const a = value as Partial<Announcement>;
  const message = a.message as Partial<Announcement["message"]> | undefined;
  if (typeof a.id !== "string" || !/^[0-9a-f]{32}$/.test(a.id)) return null;
  if (!LEVELS.includes(a.level as AnnouncementLevel)) return null;
  if (typeof a.starts_at !== "string" || !INSTANT.test(a.starts_at)) return null;
  if (typeof a.ends_at !== "string" || !INSTANT.test(a.ends_at)) return null;
  if (Date.parse(a.ends_at) <= Date.parse(a.starts_at)) return null;
  if (typeof message?.fr !== "string" || typeof message?.en !== "string" || !message.fr || !message.en) return null;
  return { id: a.id, level: a.level as AnnouncementLevel, starts_at: a.starts_at, ends_at: a.ends_at, message: { fr: message.fr, en: message.en } };
}

/** `GET /api/announcement` ; null si le serveur ne répond pas ou répond mal (la dernière annonce connue reste). */
export async function fetchAnnouncement(fetcher: typeof fetch = fetch): Promise<AnnouncementReply | null> {
  try {
    const res = await fetcher("/api/announcement", { cache: "no-store" });
    if (!res.ok) return null;
    const body = (await res.json()) as Partial<AnnouncementReply>;
    if (!body || typeof body !== "object" || !("announcement" in body)) return null;
    return { announcement: parseAnnouncement(body.announcement), server_time: typeof body.server_time === "string" ? body.server_time : "" };
  } catch {
    return null;
  }
}

// --- Temps ---------------------------------------------------------------------------------------------------------

/** Phase de l'annonce à l'instant `now` (ms) ; null finie (ou illisible). */
export function phaseAt(a: Pick<Announcement, "starts_at" | "ends_at">, now: number): Phase | null {
  const starts = Date.parse(a.starts_at);
  const ends = Date.parse(a.ends_at);
  if (Number.isNaN(starts) || Number.isNaN(ends) || now >= ends) return null;
  if (now >= starts) return "running";
  return starts - now <= IMMINENT_MS ? "imminent" : "upcoming";
}

/** Bandeau montré : annonce en cours ou à venir, sauf fermé pendant cette phase (ou une phase plus avancée). */
export function bannerShown(a: Announcement | null, now: number, dismissed: Dismissal | null): boolean {
  if (!a) return false;
  const phase = phaseAt(a, now);
  if (!phase) return false;
  return !(dismissed && dismissed.id === a.id && RANK[dismissed.phase] >= RANK[phase]);
}

/** Durée restante en clair : « dans 3 jours », « dans 2 h », « dans 1 h 20 min », « dans 10 min », « dans moins
 *  d'une minute ». */
export function countdown(ms: number): string {
  const minutes = Math.max(0, Math.floor(ms / 60_000));
  if (minutes < 1) return t("dans moins d'une minute");
  if (minutes < 60) return t("dans {n} min", { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours >= 48) return t("dans {duration}", { duration: plural(Math.floor(hours / 24), t("jour"), t("jours")) });
  const rest = minutes % 60;
  return hours < 3 && rest > 0 ? t("dans {h} h {m} min", { h: hours, m: rest }) : t("dans {h} h", { h: hours });
}

const DAY_OPTIONS: Intl.DateTimeFormatOptions = { day: "2-digit", month: "2-digit", year: "numeric", timeZone: PARIS };
const TIME_OPTIONS: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: PARIS };

function parisDay(ms: number, loc: string): string {
  return new Date(ms).toLocaleDateString(loc, DAY_OPTIONS);
}
function parisTime(ms: number, loc: string): string {
  return new Date(ms).toLocaleTimeString(loc, TIME_OPTIONS);
}

/** Créneau en clair, à l'heure de Paris : « le 24/10/2026 de 22:00 à 23:00 », ou « du 24/10/2026 à 22:00 au
 *  25/10/2026 à 02:00 ». */
export function slotText(a: Pick<Announcement, "starts_at" | "ends_at">): string {
  const starts = Date.parse(a.starts_at);
  const ends = Date.parse(a.ends_at);
  const loc = locale();
  const day = parisDay(starts, loc);
  const endDay = parisDay(ends, loc);
  return day === endDay
    ? t("le {day} de {start} à {end}", { day, start: parisTime(starts, loc), end: parisTime(ends, loc) })
    : t("du {day} à {start} au {endDay} à {end}", { day, start: parisTime(starts, loc), endDay, end: parisTime(ends, loc) });
}

/** Titre du bandeau, selon le niveau et la phase. */
export function bannerTitle(level: AnnouncementLevel, phase: Phase): string {
  if (phase === "running") return level === "outage" ? t("Interruption de service en cours") : t("Maintenance en cours");
  return level === "outage" ? t("Interruption de service prévue") : t("Maintenance prévue");
}

/** Compte à rebours du bandeau : jusqu'au début, ou jusqu'à la fin pendant la maintenance. */
export function bannerCountdown(a: Pick<Announcement, "starts_at" | "ends_at">, phase: Phase, now: number): string {
  if (phase === "running") return t("fin prévue {when}", { when: countdown(Date.parse(a.ends_at) - now) });
  return t("début {when}", { when: countdown(Date.parse(a.starts_at) - now) });
}

/** Texte de l'annonce dans la langue de l'interface. */
export function messageFor(a: Pick<Announcement, "message">): string {
  return lang() === "en" ? a.message.en : a.message.fr;
}

// --- Saisie à l'heure de Paris (fenêtre de la préproduction) -----------------------------------------------------

function parisFields(ms: number): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: PARIS,
  }).formatToParts(new Date(ms));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "00";
  return { date: `${get("year")}-${get("month")}-${get("day")}`, time: `${get("hour")}:${get("minute")}` };
}

/** Date (« AAAA-MM-JJ ») et heure (« HH:MM ») d'un instant, à l'heure de Paris : valeurs des champs de saisie. */
export function toParisInputs(ms: number): { date: string; time: string } {
  return parisFields(ms);
}

/** Instant (ms) d'une date et d'une heure saisies à l'heure de Paris ; null si la saisie est incomplète. Le
 *  décalage de Paris (heure d'été ou d'hiver) est celui du jour saisi. */
export function fromParisInputs(date: string, time: string): number | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const h = /^(\d{2}):(\d{2})$/.exec(time);
  if (!d || !h) return null;
  const wall = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Number(h[1]), Number(h[2]));
  if (Number.isNaN(wall)) return null;
  const offset = (at: number) => {
    const f = parisFields(at);
    const [y, mo, da] = f.date.split("-").map(Number);
    const [hh, mi] = f.time.split(":").map(Number);
    return Date.UTC(y!, mo! - 1, da!, hh!, mi!) - at;
  };
  let at = wall - offset(wall);
  at = wall - offset(at);
  return at;
}

/** Instant UTC à la minute, au format attendu par le serveur (« 2026-10-24T20:00:00Z »). */
export function isoMinute(ms: number): string {
  return new Date(Math.floor(ms / 60_000) * 60_000).toISOString().replace(/\.\d{3}Z$/, "Z").replace(/:\d{2}Z$/, ":00Z");
}

/** Modèle du texte, dans les deux langues (le bandeau montre celui de la langue de chaque utilisateur). */
export function templateMessages(level: AnnouncementLevel, startsMs: number, endsMs: number): { fr: string; en: string } {
  const slot = (loc: string, sameDay: string, otherDay: string) => {
    const day = parisDay(startsMs, loc);
    const endDay = parisDay(endsMs, loc);
    return (day === endDay ? sameDay : otherDay)
      .replace("{day}", day)
      .replace("{start}", parisTime(startsMs, loc))
      .replace("{endDay}", endDay)
      .replace("{end}", parisTime(endsMs, loc));
  };
  const fr = slot("fr-FR", "le {day} de {start} à {end}", "du {day} à {start} au {endDay} à {end}");
  const en = slot("en-GB", "on {day} from {start} to {end}", "from {day} at {start} to {endDay} at {end}");
  return level === "outage"
    ? {
        fr: `Une maintenance de Generex est prévue ${fr}. Le site sera indisponible pendant ce temps ; enregistrez votre travail avant.`,
        en: `Generex maintenance is scheduled ${en}. The site will be unavailable during this time; save your work beforehand.`,
      }
    : {
        fr: `Une maintenance de Generex est prévue ${fr}. Le site reste accessible ; de courtes lenteurs sont possibles.`,
        en: `Generex maintenance is scheduled ${en}. The site remains available; brief slowdowns are possible.`,
      };
}

// --- État de l'onglet -----------------------------------------------------------------------------------------------

/** Bandeau fermé : quelle annonce, et dans quelle phase (il revient à la phase suivante). */
export interface Dismissal {
  id: string;
  phase: Phase;
}

const LAST_KEY = "generex:annonce-maintenance";
const DISMISS_KEY = "generex:annonce-fermee";

/** Dernière annonce connue sur ce poste (commodité locale : montrée seulement si le serveur ne répond pas). */
export function storedAnnouncement(now: number): Announcement | null {
  try {
    const value = parseAnnouncement(JSON.parse(window.localStorage.getItem(LAST_KEY) ?? "null"));
    return value && phaseAt(value, now) ? value : null;
  } catch {
    return null;
  }
}

function storeAnnouncement(value: Announcement | null): void {
  try {
    if (value) window.localStorage.setItem(LAST_KEY, JSON.stringify(value));
    else window.localStorage.removeItem(LAST_KEY);
  } catch {
    // stockage indisponible (navigation privée) : l'annonce reste en mémoire de l'onglet
  }
}

function storedDismissal(): Dismissal | null {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(DISMISS_KEY) ?? "null") as Partial<Dismissal> | null;
    return value && typeof value.id === "string" && value.phase && value.phase in RANK ? (value as Dismissal) : null;
  } catch {
    return null;
  }
}

interface AnnouncementState {
  announcement: Announcement | null;
  /** Écart entre l'heure du serveur et celle du poste (ms). */
  offset: number;
  dismissed: Dismissal | null;
}

export const useAnnouncement = create<AnnouncementState>(() => ({ announcement: null, offset: 0, dismissed: null }));

/** Heure du serveur, d'après l'horloge du poste corrigée. */
export function serverNow(state: Pick<AnnouncementState, "offset"> = useAnnouncement.getState()): number {
  return Date.now() + state.offset;
}

/** Réponse reçue (ou null : serveur muet) : l'annonce est adoptée, ou gardée telle quelle si le serveur ne répond
 *  pas (à défaut, la copie locale du poste). */
export function announcementSeen(reply: AnnouncementReply | null, clientNow: number = Date.now()): void {
  if (!reply) {
    const state = useAnnouncement.getState();
    if (!state.announcement) {
      const stored = storedAnnouncement(clientNow + state.offset);
      if (stored) useAnnouncement.setState({ announcement: stored });
    }
    return;
  }
  const server = Date.parse(reply.server_time);
  const offset = !Number.isNaN(server) && Math.abs(server - clientNow) < MAX_OFFSET_MS ? server - clientNow : 0;
  useAnnouncement.setState({ announcement: reply.announcement, offset });
  storeAnnouncement(reply.announcement);
}

/** Fermeture du bandeau pour la session, dans la phase en cours. */
export function dismissAnnouncement(now: number = serverNow()): void {
  const a = useAnnouncement.getState().announcement;
  const phase = a ? phaseAt(a, now) : null;
  if (!a || !phase) return;
  const dismissed = { id: a.id, phase };
  useAnnouncement.setState({ dismissed });
  try {
    window.sessionStorage.setItem(DISMISS_KEY, JSON.stringify(dismissed));
  } catch {
    // stockage indisponible : fermé le temps de la page
  }
}

export interface AnnouncementWatchDeps {
  fetch: () => Promise<AnnouncementReply | null>;
  now: () => number;
  every: (run: () => void, ms: number) => () => void;
  onReturn: (run: () => void) => () => void;
  seen: (reply: AnnouncementReply | null) => void;
}

/** Relectures : tout de suite, toutes les 5 minutes, et au retour sur la page (au plus une fois par 30 s). Une à la
 *  fois ; rend l'arrêt. */
export function watchAnnouncement(deps: AnnouncementWatchDeps): () => void {
  let last = -Infinity;
  let busy = false;
  const check = async (force = false) => {
    if (busy || (!force && deps.now() - last < ANNOUNCEMENT_MIN_GAP_MS)) return;
    busy = true;
    last = deps.now();
    try {
      deps.seen(await deps.fetch());
    } catch {
      // jamais bloquant
    } finally {
      busy = false;
    }
  };
  void check(true);
  const stopTimer = deps.every(() => void check(true), ANNOUNCEMENT_EVERY_MS);
  const stopReturn = deps.onReturn(() => void check());
  return () => {
    stopTimer();
    stopReturn();
  };
}

/** Surveillance de l'onglet (navigateur). */
export function startAnnouncementWatch(): () => void {
  if (typeof window === "undefined") return () => {};
  useAnnouncement.setState({ dismissed: storedDismissal() });
  return watchAnnouncement({
    fetch: () => fetchAnnouncement(),
    now: Date.now,
    every: (run, ms) => {
      const id = window.setInterval(run, ms);
      return () => window.clearInterval(id);
    },
    onReturn: (run) => {
      const visible = () => {
        if (document.visibilityState === "visible") run();
      };
      document.addEventListener("visibilitychange", visible);
      window.addEventListener("focus", run);
      return () => {
        document.removeEventListener("visibilitychange", visible);
        window.removeEventListener("focus", run);
      };
    },
    seen: (reply) => announcementSeen(reply),
  });
}
